// Copyright (c) 2022 by Move Closer

import throttle from 'lodash/throttle'
import { ComponentInternalInstance, inject, onMounted, onUnmounted, PropType } from '@vue/composition-api'

import { defaultProvider, IS_MOBILE_PROVIDER_KEY } from '../../../support'

import { ComponentObjectPropsOptions } from '../../vue-api'
import { HeadingProps } from '../../atoms/Heading'
import { LinkProps } from '../../atoms/Link'
import { ImageProps } from '../../atoms/Image'

import { ArtisticCarouselItem, ArtisticCarouselProps } from './ArtisticCarousel.contracts'
import { MARGIN_BETWEEN_SLIDE, SLIDES_CONTAINER_SELECTOR } from './ArtisticCarousel.config'

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export const artisticCarouselProps: ComponentObjectPropsOptions<ArtisticCarouselProps> = {
  /**
   * @see ArtisticCarouselProps.heading
   */
  heading: {
    type: Object as PropType<HeadingProps>,
    required: true
  },

  /**
   * @see ArtisticCarouselProps.link
   */
  link: {
    type: Object as PropType<LinkProps>,
    required: true
  },

  /**
   * @see ArtisticCarouselProps.image
   */
  image: {
    type: Object as PropType<ImageProps>,
    required: true
  },

  /**
   * @see ArtisticCarouselProps.slides
   */
  slides: {
    type: Array as PropType<ArtisticCarouselItem[]>,
    required: true
  }
}

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export function useArtisticCarousel (props: ArtisticCarouselProps, internalInstance: ComponentInternalInstance | null) {
  if (internalInstance === null) {
    throw new Error('useArtisticCarousel(): FATAL ERROR! Failed to resolve the component instance!')
  }

  let artisticCarouselRef: HTMLDivElement | null = null
  let slidesContainerRef: HTMLDivElement | null = null
  let distanceFromTop: number = 0
  const isMobile = inject(IS_MOBILE_PROVIDER_KEY, () => defaultProvider<boolean>(false))

  const isElementInViewport = (element: HTMLDivElement): boolean => {
    const rect = element.getBoundingClientRect()
    const headerHeight = parseInt(window.document.documentElement.style.getPropertyValue('--body-margin-top')) || 0
    return (rect.top - headerHeight) <= 0 && (rect.bottom > document.documentElement.clientHeight)
  }

  const onScroll = (): void => {
    if (!artisticCarouselRef || !slidesContainerRef) {
      return
    }

    const artisticCarouselOffsetTop = artisticCarouselRef.offsetTop
    const documentScrollTop = document.documentElement.scrollTop

    const isCarouselInViewPort = isElementInViewport(artisticCarouselRef)

    const currentScrolledValue = (documentScrollTop - (distanceFromTop || 0))
    const isPlaceHolderBelowTop = artisticCarouselOffsetTop < currentScrolledValue
    const isPlaceHolderBelowBottom = (artisticCarouselOffsetTop + artisticCarouselRef.offsetHeight) > currentScrolledValue
    const canScrollHorizontally = isPlaceHolderBelowTop && isPlaceHolderBelowBottom
    const isFixedSlideShowed = (artisticCarouselRef.offsetHeight - currentScrolledValue) <= slidesContainerRef.offsetHeight

    if (isFixedSlideShowed) {
      slidesContainerRef.scrollLeft = (artisticCarouselRef.offsetHeight - slidesContainerRef.offsetHeight)
    }

    if (!canScrollHorizontally || !isCarouselInViewPort) {
      return
    }

    if (!distanceFromTop) {
      distanceFromTop = documentScrollTop
    }

    slidesContainerRef.scrollLeft = currentScrolledValue
  }

  const setCarouselHeight = (): void => {
    artisticCarouselRef = (internalInstance.refs.artisticCarouselRef as HTMLDivElement)
    if (!artisticCarouselRef) {
      return
    }

    slidesContainerRef = artisticCarouselRef.querySelector(SLIDES_CONTAINER_SELECTOR)

    const slides = (internalInstance.refs.slideRef as HTMLDivElement[])
    if (slides.length === 0) {
      return
    }

    artisticCarouselRef.style.height = `${slides.reduce((acc, slide: HTMLDivElement, index) => {
      const isLastItem = index === slides.length - 1
      const slideWidth = slide.offsetWidth + MARGIN_BETWEEN_SLIDE + (isLastItem ? slide.offsetHeight : 0)

      return acc + slideWidth
    }, 0)}px`
  }

  const throttledOnScroll = throttle(onScroll, 25)
  const throttledSetCarouselHeight = throttle(setCarouselHeight, 100)

  const registerListeners = (): void => {
    window.addEventListener('scroll', throttledOnScroll)
    window.addEventListener('resize', throttledSetCarouselHeight)
  }

  const removeListeners = (): void => {
    window.removeEventListener('scroll', throttledOnScroll)
    window.removeEventListener('resize', throttledSetCarouselHeight)
  }

  const onLoaded = () => {
    setTimeout(setCarouselHeight, 100) // fixme: probably not the best solution
  }

  onMounted(() => {
    if (isMobile()) {
      return
    }

    setCarouselHeight()
    onScroll()
    registerListeners()
  })

  onUnmounted(() => {
    removeListeners()
  })

  return { isMobile, onLoaded }
}
